import { DriveStore } from "../../store/reducers";
import { observeStore } from "../../utils/store";
import * as logger from "../../utils/logger";
import { shiftDateTime, getServerDate, getDateDifferenceInSeconds } from "../../utils/storage/date";
import { DataActions } from "../../store/data/actions";
import { isInCanada } from "hos-tools";
import scheduler from "../../utils/scheduler";
import { LOCK_SCREEN_TASK } from "../ioxDataHandler";
import { UserHosRuleSetService } from "../../model/UserHosRuleSets";
import { extend, getObjectCopy } from "../../utils/vanilla";
import { IDeviceMovement, IGpsDataPayload, IIoxDataPayload, ILastFiveSecondAvgGpsPayload } from "../../store/deviceMovement/types";
import constants, { MINUTES, SECONDS } from "../../constants";
import Movement from "../sharedServices/movement";
import { Unsubscribe } from "../../lib/redux";
import * as helpers from "./helpers";
import { SubscriptionsService } from "../base";
import Context from "../../app.context";
import { FeatureFlagName } from "../../utils/featureFlagName";
import { IFeatureFlagsService } from "../featureFlags";
import { dispatcher, DispatcherEvents } from "../../utils/dispatcher";
import { DeviceMovementActions } from "../../store/deviceMovement/actions";

export default class DrivingStatusService extends SubscriptionsService {
    private readonly observers: Unsubscribe[] = [];
    private readonly drivingThreshold: number;
    private readonly movementData: IDeviceMovement = {};

    constructor(
        private readonly store: DriveStore,
        private readonly userHosRuleSetService: UserHosRuleSetService,
        private readonly movement: Movement,
        private readonly featureFlagsService: IFeatureFlagsService,
        context: Context
    ) {
        super();

        if (context.hosProcess && isInCanada(context.hosProcess.getLatestHosRuleSetIdShortened())) {
            this.drivingThreshold = this.getStoppingThreshold();
        } else {
            this.drivingThreshold = constants.MOVEMENT.DEFAULT_DRIVING_THRESHOLD;
        }
    }

    startSubscriptions(){
        this.stopSubscriptions();

        // Clear any leftover stateUpdate from a previous service instance so observeStore
        // doesn't cache a stale value and silently ignore the next identical dispatch.
        if (this.store.getState().deviceMovement.stateUpdate) {
            this.store.dispatch(DeviceMovementActions.clearStateUpdate());
        }

        this.observers.push(observeStore(this.store, state => state.deviceMovement.pubSubPayload, this.processPubSubData.bind(this)));
        this.observers.push(observeStore(this.store, state => state.deviceMovement.ioxDataPayload, this.processIoxData.bind(this)));
        this.observers.push(observeStore(this.store, state => state.deviceMovement.stateUpdate, this.processStateUpdateData.bind(this)));

        dispatcher.on(DispatcherEvents.NewGpsData, (data: IGpsDataPayload) => {
            this.movementData.gpsDataPayload = data;
            this.processGpsData();
        });
        dispatcher.on(DispatcherEvents.NewLastFiveSecondAvgGpsData, (data: ILastFiveSecondAvgGpsPayload) => {
            this.movementData.lastFiveSecondAvgGpsPayload = data;
            this.processLastFiveSecondAvgGpsData();
        });
    }

    stopSubscriptions() {
        while (this.observers.length > 0) {
            let observer = this.observers.pop();
            observer && observer();
        }

        dispatcher.off(DispatcherEvents.NewGpsData);
        dispatcher.off(DispatcherEvents.NewLastFiveSecondAvgGpsData);
    }

    /**
     * Checks if the given speed exceeds the driving threshold
     * @param speed - The speed in km/h
     * @returns true if speed is greater than or equal to the driving threshold
     */
    private isDrivingSpeed(speed: number): boolean {
        return speed > this.drivingThreshold;
    }

    /**
     * Checks if the given speed is at or below the stopping threshold
     * @param speed - The speed in km/h
     * @returns true if speed is less than or equal to the stopping threshold
     */
    private isStoppingSpeed(speed: number): boolean {
        return speed <= this.getStoppingThreshold();
    }

    private loggingAndUpdateState(source: string, message: string, isDriving?: boolean, user?: IUser | null) {
        this.store.dispatch(DataActions.setDriving(isDriving));
        logger.event(logger.Event.MOTIONCHANGE, `Source: ${ source }. Driving = ${ isDriving }. ${ message }`, user);
    }

    /**
     * Method for determining whether or not we should allow the most recent incoming point from any of the possible sources
     * to change the current status of `state.data.driving` (i.e. to toggle the lockscreen's status).
     *
     * @param source The source of the incoming data
     * @returns A boolean value indicating whether or not we will allow the listener method to call to `loggingAndUpdateState` to update
     * `state.data.driving`
     */
    private validateIncomingData(source: "IOX" | "GPS" | "PUBSUB"): boolean {
        let { ioxDataPayload } = this.store.getState().deviceMovement;
        let gpsDataPayload = this.movementData.gpsDataPayload;
        let lastFiveSecondAvgGpsPayload = this.movementData.lastFiveSecondAvgGpsPayload;

        let fiveSecondsAgo = shiftDateTime(getServerDate(), -5 * SECONDS);
        let fifteenSecondsAgo = shiftDateTime(getServerDate(), -15 * SECONDS);

        if (source === "IOX") {
            // If the incoming IOX data is less than 5 seconds old, accept it.
            let ioxLog = (ioxDataPayload as IIoxDataPayload).newLog;
            let isIncomingIoxDataWithinAcceptableTimeRange = ioxLog.dateTime >= fiveSecondsAgo;
            if (!isIncomingIoxDataWithinAcceptableTimeRange) {
                logger.event(
                    logger.Event.MOTIONCHANGE,
                    `Ignoring incoming IOX data, Log has timestamp ${ ioxLog.dateTime.toISOString() } which is greater than 5 seconds ago (at ${ fiveSecondsAgo.toISOString() })`
                );
                return false;
            }

            return true;
        } else if (source === "GPS") {
            // For GPS, check to make sure we don't have any recent IOX data, otherwise accept it.
            if (this.store.getState().mobile.ioxAttached && ioxDataPayload) {
                let isMostRecentIoxLogWithinFifteenSeconds = ioxDataPayload.newLog.dateTime >= fifteenSecondsAgo;
                if (isMostRecentIoxLogWithinFifteenSeconds) {
                    logger.event(
                        logger.Event.MOTIONCHANGE,
                        `Ignoring incoming GPS data, IOX data has been received within the last 15 seconds (at ${ (ioxDataPayload.newLog.dateTime as Date).toISOString() })`
                    );
                    return false;
                }
            }

            return true;
        } else {
            // Ignore incoming PubSub data if there is recent IOX data..
            if (this.store.getState().mobile.ioxAttached && ioxDataPayload && ioxDataPayload.newLog.dateTime >= fifteenSecondsAgo) {
                logger.event(
                    logger.Event.MOTIONCHANGE,
                    `Ignoring incoming PubSub data, IOX data has been received within the last 15 seconds (at ${ (ioxDataPayload.newLog.dateTime as Date).toISOString() })`
                );
                return false;

            // Or there is recent GPS data..
            } else if (lastFiveSecondAvgGpsPayload && lastFiveSecondAvgGpsPayload.dateTime >= fifteenSecondsAgo.toISOString()) {
                logger.event(
                    logger.Event.MOTIONCHANGE,
                    `Ignoring incoming PubSub data, five-second average GPS data has been received within the last 15 seconds (at ${ lastFiveSecondAvgGpsPayload.dateTime })`
                );
                return false;
            } else if (gpsDataPayload && gpsDataPayload.dateTime >= fifteenSecondsAgo.toISOString()) {
                logger.event(
                    logger.Event.MOTIONCHANGE,
                `Ignoring incoming PubSub data, GPS data has been received within the last 15 seconds (at ${ gpsDataPayload.dateTime })`
                );
                return false;
            }

            return true;
        }
    }

    private processPubSubData() {
        const drivingEvent = this.store.getState().deviceMovement.pubSubPayload;
        if (!drivingEvent) {
            logger.event(logger.Event.MOTIONCHANGE, `Source: PUBSUB. No data`);
            return;
        }

        const twoMinutesAgo = shiftDateTime(getServerDate(), -2 * MINUTES);
        if (new Date(drivingEvent.dateTime) >= twoMinutesAgo) {
            if (this.validateIncomingData(helpers.SOURCE_PUBSUB)) {
                this.loggingAndUpdateState(
                    helpers.SOURCE_PUBSUB,
                    `speed=${ drivingEvent.speed } eventTimeStamp=${ drivingEvent.dateTime } ${ drivingEvent.isDriving ? "onDelay" : "offDelay" }=${ getDateDifferenceInSeconds(getServerDate(), new Date(drivingEvent.dateTime)) }`,
                    drivingEvent.isDriving
                );
            }
        } else {
            logger.event(logger.Event.MOTIONCHANGE, `Source: PUBSUB. Stale data ${ JSON.stringify(drivingEvent) }. No state change`);
        }
    }

    private processGpsData() {
        const gpsDataPayload = this.movementData.gpsDataPayload;
        if (gpsDataPayload === undefined) {
            return;
        }

        if (this.validateIncomingData(helpers.SOURCE_GPS)) {
            let kmph = gpsDataPayload.speed;

            if (this.isDrivingSpeed(kmph)) {
                this.loggingAndUpdateState(
                    helpers.SOURCE_GPS,
                    `MovementMonitor: Speed= ${ kmph } eventTimeStamp=${ gpsDataPayload.dateTime } onDelay=${ getDateDifferenceInSeconds(getServerDate(), new Date(gpsDataPayload.dateTime)) }`,
                    true
                );
            } else if (this.isStoppingSpeed(kmph)) {
                this.loggingAndUpdateState(
                    helpers.SOURCE_GPS,
                    `MovementMonitor: Speed= ${ kmph } eventTimeStamp=${ gpsDataPayload.dateTime } offDelay=${ getDateDifferenceInSeconds(getServerDate(), new Date(gpsDataPayload.dateTime)) }`,
                false
            );
            }
        }
    }

    private lastFiveSecondAvgSpeedFromGpsCheckIfMoving(payload: ILastFiveSecondAvgGpsPayload) {
        // check if the vehicle is moving based on 5 seconds avg gps speed
        const isDriving = this.store.getState().data.driving;
        if (this.isDrivingSpeed(payload.lastFiveSecondAvgGpsSpeed) && !isDriving) {
            if (this.validateIncomingData(helpers.SOURCE_GPS)) {
                this.loggingAndUpdateState(
                    helpers.SOURCE_GPS,
                    `GPS: Driving avg speed = ${ payload.lastFiveSecondAvgGpsSpeed } eventTimeStamp=${ payload.dateTime } onDelay=${ getDateDifferenceInSeconds(getServerDate(), new Date(payload.dateTime)) }`,
                        true,
                        payload.user
                    );
            }
        } else {
            logger.log(
                logger.Source.GEO,
                `Failed to set driving to true, speed < threshold or it is already in driving state. Data: ${ JSON.stringify({speed: payload.lastFiveSecondAvgGpsSpeed, isDriving}) }) }`,
                payload.user
            );
        }
    }

    private lastFiveSecondAvgSpeedFromGpsCheckIfStopped(payload: ILastFiveSecondAvgGpsPayload) {
         // checks if gps is faulty and if the vehicle is stopped based on 5 seconds avg gps speed
         if (this.store.getState().mobile.gpsConnected) {
            // eslint-disable-next-line max-len
            logger.log(logger.Source.GEO, `GPS: Silence or low speed. gpsConnected = ${ this.store.getState().mobile.gpsConnected }; Avg. speed in 5s = ${ payload.lastFiveSecondAvgGpsSpeed.toFixed(2) }`, payload.user);
            if (this.isStoppingSpeed(payload.lastFiveSecondAvgGpsSpeed)) {
                if (this.validateIncomingData(helpers.SOURCE_GPS)) {
                    this.loggingAndUpdateState(
                        helpers.SOURCE_GPS,
                        `GPS: Driving avg speed = ${ payload.lastFiveSecondAvgGpsSpeed } eventTimeStamp=${ payload.dateTime } offDelay=${ getDateDifferenceInSeconds(getServerDate(), new Date(payload.dateTime)) }`,
                        false,
                        payload.user
                    );
                }
            } else {
                // Try again
                this.movement.extendSilenceTimeout();
            }
        } else {
            // GPS not reported in watchPosition timeout. Either spotty gps while traveling, or into garage/tunnel
            this.loggingAndUpdateState(helpers.SOURCE_GPS, `GPS: Undefined. Spotty GPS. state.mobile.gpsConnected = false. eventTimeStamp=${ payload.dateTime }`, undefined, payload.user);

            let message = `GPS: Signal lost. Undefined driving. gpsConnected = ${ this.store.getState().mobile.gpsConnected }; Avg. speed in 5s = ${ payload.lastFiveSecondAvgGpsSpeed.toFixed(2) }`;
            logger.log(logger.Source.GEO, message, payload.user);

            this.movement.extendSilenceTimeout();
        }
    }

    /**
     * Lockscreen has different conditions between US & CAN and in CAN
     * it's conditions are wider than the auto-D conditions
     */
    private processIoxData() {
        const ioxDataPayload = this.store.getState().deviceMovement.ioxDataPayload;
        if (!ioxDataPayload) {
            return;
        }
        const { newLog } = ioxDataPayload;

        let isDriving = false;
        const isCurrentlyDriving = this.store.getState().data.driving;

        const vehicleStoppedMoving = () => isCurrentlyDriving && !isDriving;
        const vehicleStartedMoving = () => !isCurrentlyDriving && isDriving;

        if (isInCanada(this.userHosRuleSetService.getLatestHosRuleSetIdShortened())) {
            isDriving = newLog.roadSpeed >= this.getStoppingThreshold();
        } else {
            isDriving = newLog.roadSpeed >= helpers.FIVE_MPH;
        }

        if (vehicleStartedMoving()) {
            scheduler.unregisterTask(LOCK_SCREEN_TASK);
            if (this.validateIncomingData(helpers.SOURCE_IOX)) {
                this.loggingAndUpdateState(helpers.SOURCE_IOX, `Speed=${ newLog && newLog.roadSpeed }km/h`, true);
            }
        } else if (vehicleStoppedMoving() && !scheduler.isTaskRegistered(LOCK_SCREEN_TASK)) {
            // Validate once when registering the task (not in the callback, since it fires 5s later)
            if (this.validateIncomingData(helpers.SOURCE_IOX)) {
                scheduler.registerTask(LOCK_SCREEN_TASK, () => {
                    scheduler.unregisterTask(LOCK_SCREEN_TASK);
                    this.loggingAndUpdateState(
                        helpers.SOURCE_IOX,
                        `Speed=${ newLog && newLog.roadSpeed }km/h eventTimeStamp=${ getObjectCopy(newLog.dateTime) } offDelay=${ getDateDifferenceInSeconds(getServerDate(), new Date(getObjectCopy(newLog.dateTime))) }`,
                        false
                    );
                }, constants.CHECK_SCREEN_LOCK_INTERVAL, undefined, true);
                scheduler.start(LOCK_SCREEN_TASK);
            }
        }
    }

    private processLastFiveSecondAvgGpsData() {
        const fiveSecondAvgGpsPayload = this.movementData.lastFiveSecondAvgGpsPayload;

        if (!fiveSecondAvgGpsPayload) {
            return;
        }

        const payload = extend(true, {}, fiveSecondAvgGpsPayload);
        payload.checkingVehicleStopped ? this.lastFiveSecondAvgSpeedFromGpsCheckIfStopped(payload) : this.lastFiveSecondAvgSpeedFromGpsCheckIfMoving(payload);
    }

    private processStateUpdateData() {
        const data = this.store.getState().deviceMovement.stateUpdate;
        if (!data) {
            return;
        }
        const { source, isDriving, loggingMessage } = data;
        this.loggingAndUpdateState(source, loggingMessage, isDriving);
        this.store.dispatch(DeviceMovementActions.clearStateUpdate());
    }

    private getStoppingThreshold(): number {
        return this.featureFlagsService.getFeatureFlagValue(FeatureFlagName.LOCKSCREEN_STOPPING_THRESHOLD, constants.MOVEMENT.STOPPING_THRESHOLD) as number;
    }
}
